import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const SITE_ORIGIN = 'https://www.alloreparations.com';

const setMetaContent = (selector, content) => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => el.setAttribute('content', previous);
};

const setLinkHref = (selector, href) => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const previous = el.getAttribute('href');
  el.setAttribute('href', href);
  return () => el.setAttribute('href', previous);
};

const LegalLayout = ({ title, description, lastUpdated, children }) => {
  const location = useLocation();

  useEffect(() => {
    const fullTitle = `${title} | AlloRéparations Montpellier`;
    const previousTitle = document.title;
    document.title = fullTitle;
    window.scrollTo(0, 0);

    const url = `${SITE_ORIGIN}${location.pathname}`;
    const restorers = [
      setMetaContent('meta[name="description"]', description),
      setLinkHref('link[rel="canonical"]', url),
      setMetaContent('meta[property="og:url"]', url),
      setMetaContent('meta[property="og:title"]', fullTitle),
      setMetaContent('meta[property="og:description"]', description),
      setMetaContent('meta[name="twitter:title"]', fullTitle),
      setMetaContent('meta[name="twitter:description"]', description),
    ].filter(Boolean);

    const faqScripts = Array.from(
      document.querySelectorAll('script[type="application/ld+json"]')
    ).filter((s) => s.textContent.includes('"FAQPage"'));
    const detached = faqScripts.map((s) => ({ node: s, parent: s.parentNode }));
    detached.forEach(({ node }) => node.remove());

    return () => {
      document.title = previousTitle;
      restorers.forEach((restore) => restore && restore());
      detached.forEach(({ node, parent }) => {
        if (parent) parent.appendChild(node);
      });
    };
  }, [title, description, location.pathname]);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-100" data-testid="legal-header">
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600" />
        <div className="container-custom">
          <nav className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center group" data-testid="legal-logo-link">
              <img
                src="/logo.png"
                alt="AlloRéparations Montpellier — Réparation iPhone, Samsung, MacBook"
                width="476"
                height="205"
                className="h-11 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm transition-all"
              data-testid="legal-back-home"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Retour à l'accueil</span>
              <span className="sm:hidden">Accueil</span>
            </Link>
          </nav>
        </div>
      </header>

      <main className="bg-slate-50 min-h-screen py-12 lg:py-20">
        <div className="container-custom">
          <article className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm border border-slate-100 p-8 lg:p-14">
            <header className="mb-10 pb-8 border-b border-slate-100">
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-testid="legal-title">
                {title}
              </h1>
              {lastUpdated && (
                <p className="text-sm text-slate-500" data-testid="legal-last-updated">
                  Dernière mise à jour : {lastUpdated}
                </p>
              )}
            </header>
            <div className="legal-content prose prose-slate max-w-none">
              {children}
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default LegalLayout;
